import type { JsonObject, JsonValue, MediaAsset } from "@nexra/contracts";
import { modelInputResourceMaxBytes, uploadedMediaInput, type UploadDeliveryRegion } from "@nexra/contracts";
import { api } from "../api";
import { translateKey } from "../i18n";

export function assertInputMediaSize(file: Pick<Blob, "size" | "type">, schema?: JsonObject): void {
  const maxBytes = schema ? modelInputResourceMaxBytes(schema, file.type, "https") : undefined;
  if (maxBytes !== undefined && file.size > maxBytes) {
    throw new Error(translateKey("sandbox.inputMediaTooLarge", { maxMb: maxBytes / 1_000_000 }));
  }
}

export async function uploadInputMediaAsset(blob: Blob): Promise<MediaAsset> {
  const asset = await api<MediaAsset>("v1/assets", {
    method: "POST",
    headers: { "content-type": blob.type || "application/octet-stream" },
    body: blob,
  });
  // downloadUrl can require authentication; providers need the storage URL.
  if (asset.status !== "ready" || !asset.url || !/^https?:\/\//i.test(asset.url)) {
    throw new Error("素材上传后未返回可访问的 URL，请检查存储配置。");
  }
  return asset;
}

export async function uploadInputMediaFile(blob: Blob): Promise<string> {
  return (await uploadInputMediaAsset(blob)).url!;
}

/** Keep successful uploads for retries within one mounted playground. */
export function createInputMediaUploader() {
  const uploaded = new Map<string, { url: string; sizeBytes: number; mimeType: string }>();
  return async (input: JsonObject, region: UploadDeliveryRegion = "global", schema?: JsonObject): Promise<JsonObject> => {
    const pending = new Map<string, Promise<string>>();
    const upload = (url: string): Promise<string> => {
      const cached = uploaded.get(url);
      if (cached) { assertInputMediaSize({ type: cached.mimeType, size: cached.sizeBytes }, schema); return Promise.resolve(cached.url); }
      const existing = pending.get(url);
      if (existing) return existing;
      const work = (async () => {
        const response = await fetch(url);
        if (!response.ok) throw new Error("无法读取本地素材，请重新选择文件。");
        const blob = await response.blob();
        assertInputMediaSize(blob, schema);
        const storedUrl = await uploadInputMediaFile(blob);
        uploaded.set(url, { url: storedUrl, sizeBytes: blob.size, mimeType: blob.type });
        return storedUrl;
      })();
      pending.set(url, work);
      return work;
    };
    const visit = async (value: JsonValue): Promise<JsonValue> => {
      if (typeof value === "string" && value.startsWith("blob:")) return upload(value);
      if (Array.isArray(value)) {
        const result: JsonValue[] = [];
        for (const item of value) result.push(await visit(item));
        return result;
      }
      if (value !== null && typeof value === "object") {
        const result: JsonObject = {};
        for (const [key, item] of Object.entries(value)) result[key] = await visit(item);
        return result;
      }
      return value;
    };
    return uploadedMediaInput(await visit(input) as JsonObject, region);
  };
}
